import React, { useState } from 'react';
import {
  X,
  Mail,
  Key,
  Trash2,
  PauseCircle,
  PlayCircle,
  Server,
  RefreshCw,
  Copy,
  Check,
  CreditCard,
  Plus,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/api-client';
import type { MailboxResponse } from '@antigravity/types';

interface ManageMailboxesModalProps {
  isOpen: boolean;
  onClose: () => void;
  mailboxes: MailboxResponse[];
  onRefresh: () => void;
  onOpenCreate: () => void;
}

export const ManageMailboxesModal: React.FC<ManageMailboxesModalProps> = ({
  isOpen,
  onClose,
  mailboxes,
  onRefresh,
  onOpenCreate,
}) => {
  const [search, setSearch] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password Reset Modal State
  const [resetModalMailbox, setResetModalMailbox] = useState<MailboxResponse | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResult, setPasswordResult] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  if (!isOpen) return null;

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleToggleStatus = async (mb: MailboxResponse) => {
    setActionLoadingId(mb.id);
    setStatusMessage(null);
    try {
      if (mb.status === 'ACTIVE') {
        await apiClient.suspendMailbox(mb.id);
        setStatusMessage({ type: 'success', text: `Suspended mailbox ${mb.emailAddress}` });
      } else {
        await apiClient.activateMailbox(mb.id);
        setStatusMessage({ type: 'success', text: `Activated mailbox ${mb.emailAddress}` });
      }
      onRefresh();
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'Action failed' });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (mb: MailboxResponse) => {
    if (!window.confirm(`Are you sure you want to permanently delete mailbox ${mb.emailAddress}? All messages and Stalwart accounts will be deprovisioned.`)) {
      return;
    }

    setActionLoadingId(mb.id);
    setStatusMessage(null);
    try {
      await apiClient.deleteMailbox(mb.id);
      setStatusMessage({ type: 'success', text: `Deleted mailbox ${mb.emailAddress}` });
      onRefresh();
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'Delete failed' });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetModalMailbox) return;

    setResettingPassword(true);
    try {
      const res = await apiClient.resetMailboxPassword(
        resetModalMailbox.id,
        newPassword.trim() || undefined,
      );
      setPasswordResult(res.newPassword || newPassword || 'Password updated successfully');
      setStatusMessage({ type: 'success', text: `Password rotated for ${resetModalMailbox.emailAddress}` });
    } catch (err: any) {
      alert(`Password reset failed: ${err.message}`);
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredMailboxes = mailboxes.filter((m) =>
    m.emailAddress.toLowerCase().includes(search.toLowerCase()) ||
    (m.displayName && m.displayName.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-4xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>Manage Business Mailboxes</span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono border border-slate-700">
                  {mailboxes.length} Active Accounts
                </span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Configure mailbox credentials, passwords, active statuses, and server connections.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button
              onClick={() => {
                onClose();
                onOpenCreate();
              }}
              className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition flex items-center gap-1.5 shadow-md shadow-brand-950/40"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>+ Create Mailbox</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div
            className={`px-4 sm:px-6 py-2.5 text-xs flex items-center justify-between ${
              statusMessage.type === 'success'
                ? 'bg-emerald-950/60 text-emerald-300 border-b border-emerald-800/40'
                : 'bg-rose-950/60 text-rose-300 border-b border-rose-800/40'
            }`}
          >
            <span>{statusMessage.text}</span>
            <button onClick={() => setStatusMessage(null)} className="opacity-70 hover:opacity-100">
              Dismiss
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1 min-h-0">
          {/* Purchase Plans Banner */}
          <div className="p-4 rounded-2xl bg-gradient-to-r from-brand-950/40 to-slate-900 border border-brand-800/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="text-xs font-bold text-white flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-brand-400" />
                <span>Need More Mailbox Slots or Dedicated Senders?</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Scale your sending operations with dedicated IP pools, warmup profiles, and high-volume mailboxes.
              </p>
            </div>
            <Link
              to="/billing"
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-brand-300 border border-slate-700 hover:text-white transition flex items-center gap-1.5 shrink-0"
            >
              <span>View Mailbox Plans</span>
              <span>→</span>
            </Link>
          </div>

          {/* Mailbox List */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
              <input
                type="text"
                placeholder="Search mailboxes by address or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-72 px-3.5 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
              />
              <button
                onClick={onRefresh}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1 self-end sm:self-auto"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Refresh</span>
              </button>
            </div>

            {filteredMailboxes.length === 0 ? (
              <div className="p-10 rounded-2xl bg-slate-950/60 border border-slate-800 text-center space-y-3">
                <Mail className="w-8 h-8 text-slate-600 mx-auto" />
                <p className="text-xs text-slate-400">No mailboxes match your query.</p>
                <button
                  onClick={() => {
                    onClose();
                    onOpenCreate();
                  }}
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition inline-flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Create Your First Business Mailbox</span>
                </button>
              </div>
            ) : (
              <div className="border border-slate-800 rounded-2xl overflow-hidden bg-slate-950/40">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[640px]">
                    <thead className="bg-slate-950 text-slate-400 font-semibold uppercase tracking-wider text-[11px] border-b border-slate-800">
                      <tr>
                        <th className="py-3 px-4">Mailbox Account</th>
                        <th className="py-3 px-4">Domain</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-center">Connection</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredMailboxes.map((mb) => {
                      const domainName = mb.emailAddress.split('@')[1] || '';
                      const isLoadingThis = actionLoadingId === mb.id;

                      return (
                        <tr key={mb.id} className="hover:bg-slate-800/30 transition">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-xs text-brand-300">
                                {mb.displayName?.[0] || mb.emailAddress[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="font-semibold text-white font-mono">{mb.emailAddress}</div>
                                <div className="text-[11px] text-slate-400">{mb.displayName || 'Primary Mailbox'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-mono text-slate-400">
                            @{domainName}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                mb.status === 'ACTIVE'
                                  ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/80'
                                  : 'bg-amber-950/80 text-amber-400 border border-amber-800/80'
                              }`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current" />
                              {mb.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-[11px] text-slate-400 font-mono">
                              Port 993 (SSL) / Port 587
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* Reset Password */}
                              <button
                                onClick={() => {
                                  setResetModalMailbox(mb);
                                  setNewPassword('');
                                  setPasswordResult(null);
                                }}
                                className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition flex items-center gap-1"
                                title="Reset or rotate mailbox password"
                              >
                                <Key className="w-3 h-3 text-amber-400" />
                                <span>Change Password</span>
                              </button>

                              {/* Suspend / Activate */}
                              <button
                                onClick={() => handleToggleStatus(mb)}
                                disabled={isLoadingThis}
                                className={`p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition ${
                                  isLoadingThis ? 'animate-spin' : ''
                                }`}
                                title={mb.status === 'ACTIVE' ? 'Suspend Mailbox' : 'Activate Mailbox'}
                              >
                                {mb.status === 'ACTIVE' ? (
                                  <PauseCircle className="w-4 h-4 text-amber-400" />
                                ) : (
                                  <PlayCircle className="w-4 h-4 text-emerald-400" />
                                )}
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => handleDelete(mb)}
                                disabled={isLoadingThis}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-950/40 transition"
                                title="Delete Mailbox"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>

          {/* Connection Details Reference */}
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <div className="text-xs font-bold text-white flex items-center gap-2">
              <Server className="w-4 h-4 text-blue-400" />
              <span>Standard Client Connection Settings (Thunderbird, Apple Mail, Outlook)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono text-slate-300 mt-2">
              <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                <span className="text-[10px] text-slate-500 uppercase block font-sans font-bold">Incoming (IMAP)</span>
                <div>Server: <strong className="text-white">app-staging.axiino.com</strong></div>
                <div>Port: <strong className="text-emerald-400">993</strong> (SSL/TLS)</div>
                <div>Username: Full email address</div>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                <span className="text-[10px] text-slate-500 uppercase block font-sans font-bold">Outgoing (SMTP)</span>
                <div>Server: <strong className="text-white">app-staging.axiino.com</strong></div>
                <div>Port: <strong className="text-emerald-400">587</strong> (STARTTLS) or <strong className="text-emerald-400">25</strong></div>
                <div>Authentication: Required (Same as IMAP)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded Reset Password Modal */}
      {resetModalMailbox && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Key className="w-4 h-4 text-amber-400" />
                <span>Reset Mailbox Password</span>
              </h3>
              <button
                onClick={() => setResetModalMailbox(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-slate-400 font-mono">
              Target: <strong className="text-white">{resetModalMailbox.emailAddress}</strong>
            </p>

            {passwordResult ? (
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-emerald-950/50 border border-emerald-800/60 text-emerald-300 text-xs">
                  Password updated directly on Stalwart mail server!
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs font-mono">
                  <span className="text-brand-300">{passwordResult}</span>
                  <button
                    onClick={() => copyToClipboard(passwordResult, 'modalpass')}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    {copiedKey === 'modalpass' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <button
                  onClick={() => setResetModalMailbox(null)}
                  className="w-full py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-xs font-semibold"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleResetPasswordSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    New Password (Leave blank to generate randomly)
                  </label>
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white font-mono focus:outline-none focus:border-brand-500"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setResetModalMailbox(null)}
                    className="px-3 py-1.5 text-xs text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resettingPassword}
                    className="px-4 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-semibold flex items-center gap-1.5"
                  >
                    {resettingPassword && <Loader2 className="w-3 h-3 animate-spin" />}
                    <span>Update Password</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
